/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactElement, RefObject } from 'react';

import { Button, ChipGroup, Grid } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import OptionPill from 'bundles/onboarding-common/components/OptionPill';
import type { SelectOptionWithState } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

type BaseItem = {
  id: string;
};

type BaseSelectQuestionProps<T extends BaseItem> = {
  itemsPerRow: SelectOptionWithState<T>[][] | null;
  containerRef: RefObject<HTMLDivElement>;
  handleClick: (item: SelectOptionWithState<T>) => void;
  onViewMoreClick: () => void;
  onViewAllClick: () => void;
  isLoading: boolean;
  isExpanded: boolean;
  getLabel: (item: SelectOptionWithState<T>) => string;
  getIcon?: (item: SelectOptionWithState<T>) => ReactElement | undefined;
  ariaLabel: string;
};

const BaseSelectQuestion = <T extends BaseItem>({
  itemsPerRow,
  containerRef,
  handleClick,
  onViewMoreClick,
  onViewAllClick,
  isLoading,
  isExpanded,
  getLabel,
  getIcon,
  ariaLabel,
}: BaseSelectQuestionProps<T>) => {
  if (!itemsPerRow) {
    return null;
  }

  return (
    <Grid item xs={12} ref={containerRef}>
      {isLoading ? null : (
        <ChipGroup multiSelect={true} aria-label={ariaLabel}>
          {itemsPerRow.map((itemsRow, index) => (
            <div
              key={itemsRow[0]?.id}
              css={css`
                display: flex;
                flex-wrap: nowrap;
                overflow: hidden;
                gap: var(--cds-spacing-100);
              `}
            >
              {itemsRow.map((item) => {
                const icon = getIcon?.(item);
                if (icon) {
                  return (
                    <OptionPill
                      key={item.id}
                      label={getLabel(item)}
                      icon={icon}
                      onClick={() => handleClick(item)}
                      selected={item.isSelected}
                      variant="withImageIcon"
                    />
                  );
                }
                return (
                  <OptionPill
                    key={item.id}
                    label={getLabel(item)}
                    onClick={() => handleClick(item)}
                    selected={item.isSelected}
                    variant="textOnly"
                    spacing="small"
                  />
                );
              })}
              {index === itemsPerRow.length - 1 && (
                <Button
                  variant="ghost"
                  size="small"
                  icon={<AddIcon size="medium" />}
                  iconPosition="before"
                  onClick={isExpanded ? onViewAllClick : onViewMoreClick}
                >
                  {isExpanded ? _t('View all') : _t('View more')}
                </Button>
              )}
            </div>
          ))}
        </ChipGroup>
      )}
    </Grid>
  );
};

export default BaseSelectQuestion;
